import { useEffect, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { AlertTriangle } from 'lucide-react';
import type { FallbackRender } from '@sentry/react';

function ErrorFallback({
  resetError,
  eventId,
}: {
  error: unknown;
  componentStack: string;
  resetError: () => void;
  eventId: string;
}) {
  // P9 — keyboard-first: leva o foco direto pro CTA primário quando o boundary
  // aparece. Sem isso, mod sem mouse fica perdido depois de um crash.
  const resetRef = useRef<HTMLButtonElement>(null);
  useEffect(() => { resetRef.current?.focus(); }, []);

  function openFeedback() {
    // D6 — botão manual usa o mesmo feedbackIntegration moderno (Task 8).
    // Mantém uma só superfície visual (não mistura legacy showReportDialog).
    const feedback = Sentry.getFeedback();
    if (!feedback) return;
    const form = feedback.createForm({ formTitle: 'Conta o que aconteceu?' });
    void form.then((f) => {
      f.appendToDom();
      f.open();
    });
  }

  return (
    <div role="alert" className="min-h-screen grid place-items-center p-6 bg-bg">
      <div className="max-w-md w-full bg-bg-elevated border border-border rounded-lg p-6 space-y-4">
        <div className="flex items-center gap-2 text-destructive">
          <AlertTriangle size={20} />
          <h2 className="text-base font-semibold">Algo quebrou no Multimod</h2>
        </div>
        <p className="text-sm text-fg-muted">
          Já reportamos pra equipe. Se quiser ajudar a investigar, descreva o que
          estava fazendo — vai direto pro nosso painel.
        </p>
        {eventId && (
          <p className="text-xs text-fg-muted font-mono opacity-70">ID: {eventId}</p>
        )}
        <div className="space-y-2">
          <button
            ref={resetRef}
            type="button"
            onClick={resetError}
            className="w-full h-11 rounded-md bg-primary text-primary-fg font-medium hover:opacity-90 transition-opacity"
          >
            Tentar novamente
          </button>
          <button
            type="button"
            onClick={openFeedback}
            className="w-full h-11 rounded-md border border-border text-fg-muted hover:bg-bg-muted hover:text-fg transition-colors"
          >
            Reportar feedback
          </button>
        </div>
      </div>
    </div>
  );
}

const fallbackRenderer: FallbackRender = (props) => <ErrorFallback {...props} />;

export function SentryErrorBoundary({ children }: { children: React.ReactNode }) {
  return (
    <Sentry.ErrorBoundary fallback={fallbackRenderer}>{children}</Sentry.ErrorBoundary>
  );
}
